import type { RunnerId, SingleOpportunity } from '@ps/isp-web-services-types';
import type { Currency } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-enums';
import type { Selector } from 'reselect';

import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { PlaceBetResultCode } from '@swf/sports/types/place-bet';

import effects from './effects';
import * as baseReducers from './reducers';
import type { BetSlipState, PersistedBetSlipState, Selection } from './types';
import { getIsSelectionInBetSlip } from './utils/getIsSelectionInBetSlip';

export const initialState: BetSlipState = {
  acceptedOdds: {},
  hasInvalidCombination: false,
  isLocked: false,
  isOpen: false,
  opportunities: {
    MULTIPLE: [],
    SINGLE: []
  },
  wallets: {},
  selections: [],
  submittedOpportunities: {
    SINGLE: [],
    MULTIPLE: []
  },
  receipt: {
    SINGLE: [],
    MULTIPLE: []
  },
  rehydrated: false,
  fetchStatus: 'READY',
  selectedPromotionPicker: undefined,
  selectedPromotion: undefined,
  stakeCursorStartPosition: 0,
  stakeCursorEndPosition: 0
};

export const rehydratedInitialState: BetSlipState = {
  ...initialState,
  rehydrated: true
};

const nonAutoDismissibleResultCodes: PlaceBetResultCode[] = [
  'HISTORICAL_BALANCE_EXCEEDED',
  'ACCOUNT_CREATED_AFTER_EVENT_START',
  'BLOCKGAMEPLAY_PARALLELVERTICAL',
  'BLOCKGAMEPLAY_TIMELIMIT',
  'FAILED_GEO_COMPLY_PENDING',
  'FAILED_GEO_COMPLY_REJECTED',
  'FAILED_GEO_COMPLY_SOFTWARE_BLOCKED',
  'FAILED_GEO_COMPLY_UNKNOWN',
  'FAILED_GEO_COMPLY_USING_PROXY'
];

const selectors = (slice: Slicer<BetSlipState>, createSelector: SelectorCreator) => ({
  stakeCursorStartPosition() {
    return slice((state) => state.stakeCursorStartPosition);
  },
  stakeCursorEndPosition() {
    return slice((state) => state.stakeCursorEndPosition);
  },
  acceptedOdds() {
    return slice((state) => state.acceptedOdds);
  },
  isLocked() {
    return slice((state) => state.isLocked);
  },
  isOpen() {
    return slice((state) => state.isOpen);
  },
  opportunities() {
    return slice((state) => state.opportunities);
  },
  singleOpportunities() {
    return slice((state) => state.opportunities.SINGLE);
  },
  multipleOpportunities() {
    return slice((state) => state.opportunities.MULTIPLE);
  },
  receipt() {
    return slice((state) => state.receipt);
  },
  isBetReceiptActive() {
    return slice((state) => state.receipt.SINGLE.length || state.receipt.MULTIPLE.length);
  },
  fetchStatus() {
    return slice((state) => state.fetchStatus);
  },
  pendingSelectionCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, selectionsCount - singleOpportunities.length)
    );
  },
  pendingRemovalCount(this: {
    selectionsCount: Selector<RootState, number>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.selectionsCount,
      this.singleOpportunities,
      (selectionsCount, singleOpportunities) =>
        Math.max(0, singleOpportunities.length - selectionsCount)
    );
  },
  persistedState() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return slice<PersistedBetSlipState>(({ receipt, rehydrated, isLocked, ...state }) => state);
  },
  submittedOpportunities() {
    return slice((state) => state.submittedOpportunities);
  },
  selections() {
    return slice((state) => state.selections);
  },
  selectionsCount() {
    return slice((state) => state.selections.length);
  },
  hasSelections(this: { selectionsCount: Selector<RootState, number> }) {
    return createSelector(this.selectionsCount, (selectionsCount) => Boolean(selectionsCount));
  },
  wallets() {
    return slice((state) => state.wallets);
  },
  hasInvalidCombination() {
    return slice((state) => state.hasInvalidCombination);
  },
  errorCode() {
    return slice((state) => state.errorCode);
  },
  errorResponse() {
    return slice((state) => state.errorResponse);
  },
  hasRehydrated() {
    return slice((state) => state.rehydrated);
  },
  requiredPlacementCurrency() {
    return slice((state) => state.requiredPlacementCurrency);
  },
  isCurrencyRestricted(this: {
    requiredPlacementCurrency: Selector<RootState, Currency | undefined>;
  }) {
    return createSelector(
      this.requiredPlacementCurrency,
      (rootState: RootState) => select.account.getCurrency(rootState),
      (requiredPlacementCurrency, selectedAccountCurrency) =>
        requiredPlacementCurrency && selectedAccountCurrency
          ? requiredPlacementCurrency !== selectedAccountCurrency
          : false
    );
  },
  dailyMaxPayoutLimit() {
    return slice((state) => state.dailyMaxPayoutLimit);
  },
  isPromotionPickerOpen() {
    return createSelector(
      slice,
      (_rootState: RootState, opportunityId: string) => opportunityId,
      (state, opportunityId) => state.selectedPromotionPicker === opportunityId
    );
  },
  selectedPromotionPicker() {
    return slice((state) => state.selectedPromotionPicker);
  },
  selectedPromotion() {
    return slice((state) => state.selectedPromotion);
  },
  hasNonAutoDismissibleBetError() {
    return slice((state) =>
      state.errorResponse?.result?.find((result) =>
        nonAutoDismissibleResultCodes.includes(result.resultCode)
      )
    );
  },
  stakeInputDecimalSeparator() {
    /**
     * We default to "," to ensure the customer can always build a bet.
     * This is because the stake input regex will always allow ".",
     * therefore if we are unable to determine if the decimal seperator should only be ".",
     * we allow both "," and ".".
     */
    const defaultDecimalSeperator = ',';

    return createSelector(
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.currency.get(rootState),
      (locale, currency) => {
        try {
          const decimalSeparator =
            new Intl.NumberFormat(locale, { style: 'currency', currency })
              .formatToParts(1.1)
              .find(({ type }) => type === 'decimal')?.value ?? defaultDecimalSeperator;
          return decimalSeparator;
        } catch {
          return defaultDecimalSeperator;
        }
      }
    );
  },
  isCustomKeyboardEnabled() {
    return createSelector(
      (rootState: RootState) =>
        select.feature.check(rootState, FeatureFlag.ISPBetSlipCustomKeyboard),
      (rootState: RootState) => select.device.isPhone(rootState),
      (isFeatureFlagEnabled, isDevicePhone) => isFeatureFlagEnabled && isDevicePhone
    );
  },
  isCustomKeyboardPredefinedAmountsEnabled() {
    return createSelector(
      (rootState: RootState) =>
        select.feature.check(rootState, FeatureFlag.ISPBetSlipCustomKeyboardAmounts),
      (isFeatureFlagEnabled) => isFeatureFlagEnabled
    );
  },
  isPromotionPickerEnabled() {
    return createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPPromotionPicker),
      (rootState: RootState) => select.session.isAuthenticated(rootState),
      (rootState: RootState) => select.extraWallets.getAllEnabledWallets(rootState),
      (isFeatureFlagEnabled, isAuthenticated, allWallets) =>
        isFeatureFlagEnabled && isAuthenticated && !!allWallets.length
    );
  },
  isSelectionInTheBetSlip(this: { selections: Selector<RootState, Selection[]> }) {
    return createSelector(
      this.selections,
      (_rootState: RootState, runnerId: RunnerId) => runnerId,
      (selections, runnerId) => getIsSelectionInBetSlip(selections, runnerId)
    );
  },
  isEverySelectionInTheBetSlip(this: {
    selections: Selector<RootState, Selection[]>;
    isSelectionInTheBetSlip: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.selections,
      (_rootState: RootState, runnerIds: RunnerId[]) => runnerIds,
      (selections, runnerIds) =>
        runnerIds.every((runnerId) => getIsSelectionInBetSlip(selections, runnerId))
    );
  },
  isBetPlacementAvailable(this: {
    hasSelections: Selector<RootState, boolean>;
    singleOpportunities: Selector<RootState, SingleOpportunity[]>;
  }) {
    return createSelector(
      this.hasSelections,
      this.singleOpportunities,
      (hasSelections, singleOpportunities) =>
        hasSelections &&
        !singleOpportunities.every((singleOpportunity) =>
          ['SUSPENDED', 'UNAVAILABLE'].includes(singleOpportunity.status)
        )
    );
  },
  getSingleOpportunity(this: { singleOpportunities: Selector<RootState, SingleOpportunity[]> }) {
    return createSelector(
      this.singleOpportunities,
      (
        _state: RootState,
        selection: {
          selectionId: Selection['selectionId'];
          marketId: Selection['marketId'];
        }
      ) => selection,
      (singleOpportunities, selection) =>
        singleOpportunities.find(
          (singleOpportunity) =>
            singleOpportunity.runnerId.marketId === selection.marketId &&
            singleOpportunity.runnerId.selectionId === selection.selectionId
        )
    );
  }
});

const authUpdatePending = (state: BetSlipState): BetSlipState => ({
  ...state,
  fetchStatus: 'AUTH_UPDATE_PENDING'
});

const reducers = {
  ...baseReducers,
  'session/response': authUpdatePending,
  'session/clear': authUpdatePending
};

interface BetSlipModel extends ModelConfig<BetSlipState> {
  selectors: typeof selectors;
  reducers: typeof reducers;
  effects: typeof effects;
}

const betSlip: BetSlipModel = {
  state: { ...initialState },
  selectors,
  reducers,
  effects
};

export default betSlip;
